import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { listSaved, toggleSaved, type SavedKind } from "../lib/saved";

interface SavedState {
  isSaved: (kind: SavedKind, id: string) => boolean;
  savedIds: (kind: SavedKind) => string[];
  toggle: (kind: SavedKind, id: string) => Promise<boolean>;
}

const Ctx = createContext<SavedState | null>(null);

export function SavedProvider({ children }: { children: ReactNode }) {
  const [jobs, setJobs] = useState<Set<string>>(new Set());
  const [loads, setLoads] = useState<Set<string>>(new Set());

  useEffect(() => {
    listSaved()
      .then((r) => { setJobs(new Set(r.jobs)); setLoads(new Set(r.loads)); })
      .catch(() => undefined);
  }, []);

  const setFor = useCallback((kind: SavedKind) => (kind === "jobs" ? setJobs : setLoads), []);

  const toggle = useCallback(async (kind: SavedKind, id: string) => {
    const set = kind === "jobs" ? jobs : loads;
    const willSave = !set.has(id);
    // Optimistic: reflect immediately, reconcile with the server result.
    setFor(kind)((prev) => {
      const next = new Set(prev);
      if (willSave) next.add(id); else next.delete(id);
      return next;
    });
    try {
      const { saved } = await toggleSaved(kind, id);
      setFor(kind)((prev) => {
        const next = new Set(prev);
        if (saved) next.add(id); else next.delete(id);
        return next;
      });
      return saved;
    } catch {
      // Revert on failure.
      setFor(kind)((prev) => {
        const next = new Set(prev);
        if (willSave) next.delete(id); else next.add(id);
        return next;
      });
      return set.has(id);
    }
  }, [jobs, loads, setFor]);

  const value = useMemo<SavedState>(() => ({
    isSaved: (kind, id) => (kind === "jobs" ? jobs : loads).has(id),
    savedIds: (kind) => [...(kind === "jobs" ? jobs : loads)],
    toggle,
  }), [jobs, loads, toggle]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSaved(): SavedState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useSaved must be used within SavedProvider");
  return ctx;
}
